import type { FC } from 'react'
import { useState } from 'react'

import { NotificationBubble, useSwipeable } from '@skytech/ui'
import CellphoneIcon from 'mdi-react/CellphoneIcon'
import CreditCardOutlineIcon from 'mdi-react/CreditCardOutlineIcon'
import FaceAgentIcon from 'mdi-react/FaceAgentIcon'
import FolderOpenIcon from 'mdi-react/FolderOpenIcon'
import HomeIcon from 'mdi-react/HomeIcon'
import MenuIcon from 'mdi-react/MenuIcon'
import ShoppingIcon from 'mdi-react/ShoppingIcon'
import ToolsIcon from 'mdi-react/ToolsIcon'
import { Link } from 'react-router-dom'

import { useAppContext } from '@/App.context'
import { HandshakeIcon } from '@/assets/gfx/icons/HandshakeIcon'
import { Translation } from '@/components/i18n/Translation'
import useBetaAccess from '@/hooks/beta-access'
import { appRoutes } from '@/util/misc'
import { useNotifications } from '@/util/useNotifications'

import './navigation-mobile.scss'
import './navigation-desktop.scss'

const OrdersLinkContent: FC = () => {
  const { newOrdersAmount } = useNotifications()

  return (
    <>
      <Translation id="Orders" />

      {newOrdersAmount > 0 && <NotificationBubble isDealer amount={newOrdersAmount} />}
    </>
  )
}

const InjuryReportsLinkContent: FC = () => {
  const { pendingInjuryReportAmount } = useNotifications()

  return (
    <>
      <Translation id="Injury Reports" />

      {pendingInjuryReportAmount > 0 && (
        <NotificationBubble isDealer amount={pendingInjuryReportAmount} />
      )}
    </>
  )
}

const TradeInOrdersLinkContent: FC = () => {
  const { pendingTradeInOrderActions } = useNotifications()

  return (
    <>
      <Translation id="Trade In" />

      {pendingTradeInOrderActions > 0 && (
        <NotificationBubble isDealer amount={pendingTradeInOrderActions} />
      )}
    </>
  )
}

const useLinks = () => {
  const {
    hasOrders,
    hasCreditCheck,
    hasInjuryReports,
    hasCustomers,
    hasCreditRequest,
    hasSalesSupport,
    hasTradeInAccess,
  } = useBetaAccess()

  return [
    {
      route: appRoutes.home,
      Icon: HomeIcon,
      content: <Translation id="Home" />,
    },
    hasCustomers && {
      route: appRoutes.customers,
      Icon: HandshakeIcon,
      content: <Translation id="Customers" />,
    },
    hasOrders && {
      route: appRoutes.orders,
      Icon: FolderOpenIcon,
      content: <OrdersLinkContent />,
    },
    hasInjuryReports && {
      route: appRoutes.injuryReports,
      Icon: ToolsIcon,
      content: <InjuryReportsLinkContent />,
    },
    hasCreditCheck && {
      route: appRoutes.creditCheck,
      Icon: ShoppingIcon,
      content: <Translation id="Credit Check" />,
    },
    hasCreditRequest && {
      route: appRoutes.creditRequest,
      Icon: CreditCardOutlineIcon,
      content: <Translation id="Credit Request" />,
    },
    hasTradeInAccess && {
      route: appRoutes.tradeInOrders,
      Icon: CellphoneIcon,
      content: <TradeInOrdersLinkContent />,
    },
    hasSalesSupport && {
      route: appRoutes.salesSupport,
      Icon: FaceAgentIcon,
      content: <Translation id="Sales support" />,
    },
  ].filter(
    (link): link is { route: string; Icon: React.ComponentType; content: JSX.Element } => !!link
  )
}

const Navigation = () => {
  const [visible, setVisible] = useState(false)

  const handlers = useSwipeable({ onSwipedLeft: () => setVisible(false) })
  const { whiteLabel } = useAppContext()
  const links = useLinks()

  return (
    <nav
      className={`navigation wl-${whiteLabel} ${visible ? 'is-visible' : 'is-hidden'}`}
      {...handlers}
    >
      <ul>
        {/* @ts-ignore */}
        {links.map(({ route, Icon, content }, idx) => (
          <li key={idx}>
            <Link to={route} onClick={() => setVisible(false)}>
              <Icon />

              {content}
            </Link>
          </li>
        ))}
      </ul>

      <button
        className={`toggler ${visible ? 'is-hidden' : 'is-visible'}`}
        onClick={() => setVisible(!visible)}
      >
        <MenuIcon />
      </button>
    </nav>
  )
}

export default Navigation
